import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, BookOpen, Timer, Brain, Heart, Moon, Target, Zap, CheckCircle } from "lucide-react";
import heroImage from "@/assets/blog/test-preparation-hero.jpg";
import stopProcrastinationImage from "@/assets/blog/stop-procrastination-hero.jpg";
import digitalToolsImage from "@/assets/blog/digital-tools-hero.jpg";

const tocItems = [
  { id: "setting-foundation", title: "Setting the Foundation" },
  { id: "study-methods", title: "Study Methods that I Actually Use" },
  { id: "managing-time-energy", title: "Managing Time and Energy" },
  { id: "self-care-mindset", title: "Self-Care and Mindset" },
  { id: "final-48-hours", title: "The Final 48 Hours" },
  { id: "conclusion", title: "Conclusion" },
];

const PreparingForMajorTest = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Preparing for a Major Test? Here are Some Things I Did that Made a Huge Difference"
        description="Learn proven test preparation strategies that actually work. From creating realistic study plans to managing time and energy, discover methods that made a huge difference in my exam results."
        keywords="test preparation, exam study tips, study strategies, pomodoro technique, active recall, spaced repetition, CSW, online study rooms, exam preparation"
        canonicalUrl="/blog/preparing-for-major-test/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Study Tips
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>December 16, 2024</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>12 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              Preparing for a Major Test? Here are Some Things I Did that Made a Huge Difference
            </h1>

            {/* Featured Image */}
            <img 
              src={heroImage} 
              alt="Student studying with textbooks, notes, and flashcards for exam preparation" 
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
            
              {/* Lead Paragraph */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                Preparing for a major test can feel overwhelming, especially when the stakes are high and the pressure to perform well is intense. Over the years, I've learned that success isn't just about how many hours you spend studying, but about the strategies and habits you build leading up to the exam.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                There were a lot of times where I was pouring everything I had into taking a test, but ended up getting a poor result. I realized that your study habits as well as the strategies you use break or make your exams.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                In this article, I'll share some of the key things I did that made a huge difference in my own test preparations—methods that helped me study smarter, stay focused, and enter the exam room confident and ready. Whether it's your first big test or one of many, these tips can be adapted to fit your style and help you achieve your best.
              </p>

              {/* Mobile TOC */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

            {/* Section 1: Setting the Foundation */}
            <div id="setting-foundation" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Setting the Foundation
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The first and most important step I took was to create a realistic study plan. The key word here is <strong>realistic</strong>. Don't expect that you could finish one lesson in just one hour. Everybody works differently.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              So, here's what you're going to do. First, you need to time yourself as you go through one lesson. Try to do this without any interruptions. Just pure studying. Once finished, you now have a reliable baseline of how long you can study. Then, do this again, this time using whatever study method you prefer, and time yourself. Now, you have an actual realistic time of how long it takes you to finish one lesson. Doing this makes scheduling study time much easier.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              What's the next thing I do? Instead of trying to cover everything at once, I broke down the material into manageable chunks and set clear daily and weekly goals. When it comes to my study method, I use the pomodoro technique through <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW</a>. Almost everything that is directed towards my study prep goes through CSW. My most used feature in their website is their built-in pomodoro timer as well as their 24/7 online study room.
            </p>

            <div className="bg-muted/50 rounded-xl p-6 mb-10">
              <h3 className="font-display font-bold text-lg mb-4">CSW Features I Use for Test Prep:</h3>
              <ul className="space-y-2 text-foreground/90 font-body">
                <li className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-primary" />
                  Built-in pomodoro timer with customizable intervals
                </li>
                <li className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  Task management tools for progress tracking
                </li>
                <li className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-primary" />
                  Built-in note section for easy access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  24/7 online study rooms for accountability
                </li>
              </ul>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Organizing my study materials early on made a big difference too. I gathered all my notes, textbooks, and any online resources in one place, which saved me from scrambling for materials later. I also created simple flashcards for quick review and used digital tools to keep track of what I had studied and what needed more attention.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Lastly, the most important thing is to be consistent with your habits before the test date. Try to establish a regular study schedule to ensure that your mind stays sharp and make a habit of reviewing notes daily rather than just cramming it.
            </p>

            {/* Section 2: Study Methods */}
            <div id="study-methods" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Study Methods that I Actually Use
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              One of the biggest changes in my preparation came from shifting how I studied. Basically shifting from passive learning to active learning. Instead of just rereading my notes or rewriting them, I focused on techniques that made information much easier for me to remember. If you struggle with getting started, check out our guide on <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>how to stop procrastination</Link>.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Active Recall</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    My favorite study method. This means constantly testing yourself without looking at the answers. Active recall begins the moment you start writing notes. Instead of writing notes passively, try to write them in interrogative form or in ways that you think your teacher would test you. When it comes to subjects that are focused on identification or knowledge-based answers, active recall helped strengthen my memory and made it easier for me to retrieve information during the test. Try using <Link to="/flash-cards/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>flashcards</Link> to practice active recall.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Spaced Repetition</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    This means reviewing material at increasing intervals rather than cramming. This approach made sure that the lessons were fresh in my head and also reduced burnout. To make this process even more effective, I found tools and platforms that supported my personalized practice sessions like CSW. Explore our <Link to="/quizzes/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>quiz tools</Link> for self-testing.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Practice Tests Under Exam Conditions</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    Simulating the actual test environment helps you manage your time better and also reduces test anxiety (I know I need that). Additionally, explaining difficult concepts out loud, even if you look crazy, clarified my understanding and revealed gaps in my knowledge.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: Managing Time and Energy */}
            <div id="managing-time-energy" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Managing Time and Energy
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              This is an important part that most people forget about. Managing time and energy during test preparation is crucial to maintaining focus and avoiding burnout.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              I found that breaking my study sessions into focused blocks, often using techniques like the Pomodoro method, where I studied for 25 to 50 minutes followed by short breaks, helped me stay sharp and productive. These regular pauses let my brain rest and prevented fatigue from creeping in during long study hours.
            </p>

            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-10">
              <h3 className="font-display font-bold text-lg mb-3">The Reward System</h3>
              <p className="text-muted-foreground font-body">
                Once I completed 4 sessions of 25-minute focus sessions, I get to reward myself with a 15-minute break. This forces your brain to get fixated on getting that "reward" and keeps you motivated throughout longer study sessions.
              </p>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              To keep things fresh, I mixed up subjects and topics throughout the day instead of sticking to just one area for hours. This helped keep my mind engaged and made the process less monotonous. I also learned to schedule lighter review sessions closer to the test date, moving away from last-minute cramming and allowing myself time to consolidate what I had already learned.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              In this regard, I found digital tools and study management platforms like <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW</a> particularly helpful. CSW's approach to structured, bite-sized learning combined with real-time progress tracking meant I could optimize my study schedule around my own energy levels.
            </p>

            {/* Section 4: Self-Care and Mindset */}
            <div id="self-care-mindset" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Self-Care and Mindset
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Another often overlooked part when it comes to preparing for a major exam is taking care of your body and mind. This is super essential when it comes to my test preparation strategy.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              You're probably tired of already hearing it, but there's a reason why it keeps repeating itself. And that is to get enough sleep or at least try to maintain a consistent sleep pattern. If you can't get the recommended 8 hours of sleep, try to maintain a specified number of hours every time. I try to eat nutritious meals also to keep my energy levels stable. It became clear that no amount of studying could compensate for exhaustion or poor health.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Managing stress was another key factor. I incorporated quick relaxation techniques such as deep breathing exercises, short walks, or brief breaks to clear my head whenever I felt overwhelmed. These small moments of self-care allowed me to reset and return to studying with a calmer, more focused mind.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Equally important was cultivating a positive mindset. One thing that I do is basically join 24/7 online study sessions through CSW. Aside from being an online workspace, CSW also has a 24/7 virtual study room where you can join anytime and anywhere. These quiet sessions with the presence of others made it easy for me to do late night study sessions (which are oftentimes unavoidable!). Aside from that, joining makes it easier for me to be held accountable. You wouldn't want anyone seeing you scroll through your phone now would you?
            </p>

            {/* Section 5: The Final 48 Hours */}
            <div id="final-48-hours" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Moon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    The Final 48 Hours
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              In the last 48 hours before the test, instead of forcing myself to learn or memorize materials, I shift my focus into reinforcing what I already know. This meant concentrating on key summaries, important information and concepts that I keep forgetting or messing up.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Rather than attempting to cover everything, I prioritized on things that I feel like my memory is weak. Most importantly, this time, try to get a full night's rest before the test. Getting adequate sleep helps your brain process the information as well and keeps you focused on exam day!
            </p>

            {/* Conclusion */}
            <h2 id="conclusion" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Conclusion
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              So, that's everything I do when it comes to preparing for a major test. I know these things take time and consistency, but that's exactly why they're game changers when it comes to taking a test. Starting early and preparing early is exactly what helps you ace your test compared to students who start the night before your exams. While they're cramming information and drowning themselves in coffee, you'd already be in bed by then. That's it! Hope you ace your test!
            </p>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 md:p-12 text-center">
              <BookOpen className="w-12 h-12 text-primary mx-auto mb-6" />
              <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">
                Ready to Ace Your Next Test?
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-8 max-w-xl mx-auto">
                Join thousands of students using CSW's free online study rooms and productivity tools to prepare for exams with focus and accountability.
              </p>
              <a 
                href="https://app.csw.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-colors"
              >
                Try CSW for Free
              </a>
            </div>

            {/* Related Articles Section */}
            <div className="mt-20">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 tracking-tight">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Link 
                  to="/blog/how-to-stop-procrastination/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <img 
                    src={stopProcrastinationImage} 
                    alt="How to stop procrastination" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-5">
                    <span className="text-sm text-primary font-medium font-body mb-2 block">Productivity</span>
                    <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                      How to Stop Procrastination
                    </h3>
                  </div>
                </Link>

                <Link 
                  to="/blog/top-10-digital-tools-for-academic-success/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <img 
                    src={digitalToolsImage} 
                    alt="Digital tools for academic success" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-5">
                    <span className="text-sm text-primary font-medium font-body mb-2 block">Study Tips</span>
                    <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                      My Top 10 Essential Digital Tools for Academic Success
                    </h3>
                  </div>
                </Link>
              </div>
            </div>

          </div>
          
          {/* Floating Sticky TOC - Desktop only */}
          <aside className="hidden lg:block fixed right-8 xl:right-16 top-24 z-40">
            <BlogTableOfContents items={tocItems} collapsible />
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default PreparingForMajorTest;